import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const secureStorage = {
  setItem(key, value) {
    localStorage.setItem(key, value);
  },
  getItem(key) {
    return localStorage.getItem(key);
  },
  removeItem(key) {
    localStorage.removeItem(key);
  }
};

// Fonction pour vérifier si un token JWT est expiré
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Erreur lors de la vérification du token:', error);
    return true;
  }
};

// Vérification initiale synchrone de l'authentification
const getInitialAuthState = () => {
  try {
    const tokensString = secureStorage.getItem('auth_tokens');
    if (tokensString) {
      const tokens = JSON.parse(tokensString);
      // Vérifier si le token d'accès est valide
      if (tokens.access && !isTokenExpired(tokens.access)) {
        return { isAuthenticated: true, isLoading: false };
      }
      // Si le token d'accès est expiré mais qu'on a un refresh token valide
      if (tokens.refresh && !isTokenExpired(tokens.refresh)) {
        return { isAuthenticated: true, isLoading: false }; // Considérer comme authentifié si refresh token valide
      }
    }
    return { isAuthenticated: false, isLoading: false };
  } catch (error) {
    console.error('Error checking initial auth state:', error);
    return { isAuthenticated: false, isLoading: false };
  }
};

const initialAuthState = getInitialAuthState();

const useGlobale = create(
  persist(
    (set, get) => ({
      authenticated_fonap: initialAuthState.isAuthenticated,
      user: null,
      isAuthLoading: false, // Toujours false à l'initialisation

      updateUserInfo: (updatedUserData) => {
        set(state => ({
          ...state,
          user: {
            ...state.user,
            ...updatedUserData
          }
        }));
      },

      setAuthLoading: (loading) => {
        console.log('Setting auth loading to:', loading);
        set({ isAuthLoading: loading });
      },

      login: (tokens, userInfo) => {
        try {
          // Stocker les tokens
          secureStorage.setItem('auth_tokens', JSON.stringify(tokens));
          
          const safeUserInfo = { ...userInfo };
          if (safeUserInfo.password) delete safeUserInfo.password;
          
          set({
            authenticated_fonap: true,
            user: safeUserInfo,
            isAuthLoading: false,
          });
        } catch (error) {
          console.error('Error during login:', error);
          set({ isAuthLoading: false });
        }
      },

      refreshUserData: async () => {
        try {
          const { default: api } = await import('./api');
          const response = await api.get('/users/profile/');
          const userData = response.data;
          
          set(state => ({
            ...state,
            user: {
              ...state.user,
              ...userData
            }
          }));
          
          return userData;
        } catch (error) {
          console.error('Error refreshing user data:', error);
          return null;
        }
      },

      logout: async () => {
        try {
          secureStorage.removeItem('auth_tokens');
          set({
            authenticated_fonap: false,
            user: null,
            isAuthLoading: false,
          });
          // NE PAS rediriger ici - laisser React Router gérer cela
        } catch (error) {
          console.error('Error during logout:', error);
        }
      },

      checkAuth: async () => {
        console.log('checkAuth called');
        try {
          const tokensString = secureStorage.getItem('auth_tokens');
          console.log('Tokens found:', !!tokensString);
          
          if (tokensString) {
            const tokens = JSON.parse(tokensString);
            
            // Vérifier si le token d'accès est valide
            if (tokens.access && !isTokenExpired(tokens.access)) {
              console.log('Access token is valid');
              set({ 
                authenticated_fonap: true,
                isAuthLoading: false 
              });
              return true;
            }
            
            // Si le token d'accès est expiré, vérifier le refresh token
            if (tokens.refresh && !isTokenExpired(tokens.refresh)) {
              console.log('Access token expired but refresh token is valid');
              // Garder l'utilisateur connecté, l'intercepteur API gérera le refresh
              set({ 
                authenticated_fonap: true,
                isAuthLoading: false 
              });
              return true;
            }
            
            console.log('Both tokens are expired');
            // Les deux tokens sont expirés
            secureStorage.removeItem('auth_tokens');
          }
          
          console.log('No valid tokens found');
          // Aucun token valide
          set({ 
            authenticated_fonap: false, 
            user: null,
            isAuthLoading: false 
          });
          return false;
        } catch (error) {
          console.error('Error checking auth:', error);
          set({ 
            authenticated_fonap: false, 
            user: null,
            isAuthLoading: false 
          });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        authenticated_fonap: state.authenticated_fonap,
        user: state.user,
      }),
    }
  )
);

export default useGlobale;